import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { LogOut, Users, Plus, Trash2, Edit, Key, Download, X, FileSpreadsheet, Ticket, Phone, CheckCircle, XCircle, AlertTriangle, UserPlus, Wrench, Trophy, MapPin, Clock, Bell, ListChecks, Wifi } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../components/ui/dialog';
import TicketCard from '../components/TicketCard';
import ConnectionsPanel from '../components/ConnectionsPanel';
import D2DPanel from '../components/D2DPanel';
import {
  StatisticsFilters,
  TechnicianLeaderboard,
  OperatorLeaderboard,
  ServiceStatsCards,
  ServiceStatusBreakdown,
  CategoryBreakdown,
  ConnectionStatsCards,
  ConnectionStatusBreakdown,
  ConnectionByGroupBreakdown,
  D2DStatsCards,
  D2DTopAgents,
  ScoringRules,
  TicketManagementPanel,
  UserManagementPanel,
  AdminGroupsPanel,
  TechnicianPerformancePanel
} from '../components/dashboard';
import MapPanel from '../components/dashboard/MapPanel';
import HRPanel from '../components/dashboard/HRPanel';
import WorkZonesPanel from '../components/dashboard/WorkZonesPanel';
import TasksPanel from '../components/tasks/TasksPanel';
import OnlinePanel from '../components/network/OnlinePanel';
import BroadcastPanel from '../components/dashboard/BroadcastPanel';
import AccountingPanel from '../components/dashboard/AccountingPanel';
import NotificationBell from '../components/dashboard/NotificationBell';
import TopBarTasksButton from '../components/tasks/TopBarTasksButton';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AdminDashboard({ user, onLogout, readOnly = false }) {
  const [statistics, setStatistics] = useState(null);
  const [connectionStats, setConnectionStats] = useState(null);
  const [d2dStats, setD2dStats] = useState(null);
  const [scores, setScores] = useState([]);
  const [operatorScores, setOperatorScores] = useState([]);
  const [users, setUsers] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [technicians, setTechnicians] = useState([]);
  const [groups, setGroups] = useState([]);
  const [myAdminGroups, setMyAdminGroups] = useState([]);
  const [selectedGroupIds, setSelectedGroupIds] = useState(['all']); // Multiple selection support
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('today');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [displayDateRange, setDisplayDateRange] = useState('');
  const [showCreateUserDialog, setShowCreateUserDialog] = useState(false);
  const [showEditUserDialog, setShowEditUserDialog] = useState(false);
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showTechTicketsDialog, setShowTechTicketsDialog] = useState(false);
  const [selectedTechnician, setSelectedTechnician] = useState(null);
  const [technicianTickets, setTechnicianTickets] = useState(null);
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [rejectTicket, setRejectTicket] = useState(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [newUser, setNewUser] = useState({
    username: '',
    password: '',
    full_name: '',
    phone: '',
    email: '',
    role: 'technician',
    roles: [],
    groups: [],
    modules: []
  });
  const [editUser, setEditUser] = useState({
    full_name: '',
    phone: '',
    email: '',
    roles: [],
    groups: []
  });
  const [newPassword, setNewPassword] = useState('');
  const [activeTab, setActiveTab] = useState('statistics');
  const [statsSubTab, setStatsSubTab] = useState('services');

  const token = localStorage.getItem('token');

  const calculateDateRange = (selectedPeriod) => {
    const now = new Date();
    let start, end;

    switch (selectedPeriod) {
      case 'today':
        start = new Date(now.setHours(0, 0, 0, 0));
        end = new Date();
        break;
      case 'yesterday':
        const yesterday = new Date(now);
        yesterday.setDate(yesterday.getDate() - 1);
        start = new Date(yesterday.setHours(0, 0, 0, 0));
        end = new Date(yesterday.setHours(23, 59, 59, 999));
        break;
      case 'last_7_days':
        start = new Date(now);
        start.setDate(start.getDate() - 7);
        end = new Date();
        break;
      case 'last_30_days':
        start = new Date(now);
        start.setDate(start.getDate() - 30);
        end = new Date();
        break;
      case 'current_month':
        start = new Date(now.getFullYear(), now.getMonth(), 1);
        end = new Date();
        break;
      case 'last_month':
        start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        end = new Date(now.getFullYear(), now.getMonth(), 0, 23, 59, 59);
        break;
      case 'custom':
        if (startDate && endDate) {
          start = new Date(startDate);
          end = new Date(endDate);
        }
        break;
      default:
        start = new Date(now);
        start.setDate(start.getDate() - 7);
        end = new Date();
    }

    if (start && end) {
      const formatDate = (date) => {
        return date.toLocaleDateString('az-AZ', { year: 'numeric', month: '2-digit', day: '2-digit' });
      };
      setDisplayDateRange(`${formatDate(start)} - ${formatDate(end)}`);
    }
  };

  const fetchData = async () => {
    try {
      // Calculate date range display
      calculateDateRange(period);
      
      let statsUrl = `${API}/admin/statistics`;
      let techScoresUrl = `${API}/technicians/scores`;
      let opScoresUrl = `${API}/operators/scores`;
      const params = new URLSearchParams();
      
      if (period !== 'custom') {
        params.append('period', period);
      } else if (period === 'custom' && startDate && endDate) {
        params.append('start_date', startDate);
        params.append('end_date', endDate);
      }
      
      // Add group filter if selected (support multiple groups)
      if (selectedGroupIds.length > 0 && !selectedGroupIds.includes('all')) {
        // For multiple groups, we'll use comma-separated values
        params.append('group_ids', selectedGroupIds.join(','));
      }
      
      if (params.toString()) {
        statsUrl += `?${params.toString()}`;
        techScoresUrl += `?${params.toString()}`;
        opScoresUrl += `?${params.toString()}`;
      }
      
      const [statsRes, techScoresRes, opScoresRes, connStatsRes, d2dStatsRes] = await Promise.all([
        axios.get(statsUrl, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(techScoresUrl, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(opScoresUrl, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/connections/stats${params.toString() ? '?' + params.toString() : ''}`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: null })),
        axios.get(`${API}/d2d/stats${params.toString() ? '?' + params.toString() : ''}`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: null }))
      ]);
      setStatistics(statsRes.data);
      setScores(techScoresRes.data);
      setOperatorScores(opScoresRes.data);
      setConnectionStats(connStatsRes.data);
      setD2dStats(d2dStatsRes.data);
    } catch (error) {
      toast.error('Məlumatları yükləmək mümkün olmadı');
    }
  };

  const fetchTickets = async () => {
    try {
      const [ticketsRes, techniciansRes] = await Promise.all([
        axios.get(`${API}/tickets`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/technicians`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setTickets(ticketsRes.data);
      setTechnicians(techniciansRes.data);
    } catch (error) {
      toast.error('Ticketləri yükləmək mümkün olmadı');
    }
  };

  const handleAssignTechnician = async (ticketId, technicianId) => {
    try {
      await axios.patch(
        `${API}/tickets/${ticketId}`,
        { assigned_technician_id: technicianId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Texnikə təyin edildi');
      fetchTickets();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Təyin etmək mümkün olmadı';
      toast.error(errorMsg);
    }
  };

  const handleCloseTicket = async (ticketId) => {
    try {
      await axios.patch(
        `${API}/tickets/${ticketId}`,
        { status: 'closed' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Ticket bağlandı');
      fetchTickets();
      fetchData();
    } catch (error) {
      toast.error('Bağlamaq mümkün olmadı');
    }
  };

  const handleShowRejectDialog = (ticket) => {
    setRejectTicket(ticket);
    setRejectionReason('');
    setShowRejectDialog(true);
  };

  const handleRejectTicket = async () => {
    if (!rejectTicket) return;
    
    try {
      await axios.patch(
        `${API}/tickets/${rejectTicket.id}`,
        { status: 'rejected', rejection_reason: rejectionReason },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Ticket geri qaytarıldı');
      fetchTickets();
      fetchData();
      setShowRejectDialog(false);
      setRejectTicket(null);
      setRejectionReason('');
    } catch (error) {
      toast.error('Geri qaytarmaq mümkün olmadı');
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/admin/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(response.data);
    } catch (error) {
      toast.error('İstifadəçiləri yükləmək mümkün olmadı');
    }
  };
  
  const fetchMyAdminGroups = async () => {
    try {
      const response = await axios.get(`${API}/groups/my-admin-groups`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMyAdminGroups(response.data);
    } catch (error) {
      console.error('Admin groups fetch error:', error);
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    try {
      // Create user
      const createData = { ...newUser };
      const selectedGroups = createData.groups || [];
      delete createData.groups;
      
      // Set primary role from roles array
      if (createData.roles && createData.roles.length > 0) {
        createData.role = createData.roles[0];
      }
      
      const userRes = await axios.post(`${API}/auth/register`, createData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Add user to selected groups
      const userId = userRes.data.id;
      for (const groupId of selectedGroups) {
        try {
          await axios.post(`${API}/groups/${groupId}/members/${userId}`, {}, {
            headers: { Authorization: `Bearer ${token}` }
          });
        } catch (groupError) {
          console.error(`Failed to add user to group ${groupId}:`, groupError);
        }
      }
      
      toast.success('İstifadəçi yaradıldı');
      setShowCreateUserDialog(false);
      setNewUser({ username: '', password: '', full_name: '', phone: '', email: '', role: 'technician', roles: [], groups: [], modules: [] });
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'İstifadəçi yaratmaq mümkün olmadı');
    }
  };

  const handleEditUser = async (e) => {
    e.preventDefault();
    try {
      const { groups, roles, ...basicData } = editUser;
      
      // Add roles to update data
      const updateData = { ...basicData };
      if (roles && roles.length > 0) {
        updateData.roles = roles;
      }
      
      // Update basic user data including roles
      await axios.patch(`${API}/admin/users/${selectedUser.id}`, updateData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Update groups - remove from old groups, add to new groups
      const oldGroups = selectedUser.groups || [];
      const newGroups = groups || [];
      
      // Remove from groups that are no longer selected
      for (const groupId of oldGroups) {
        if (!newGroups.includes(groupId) && myAdminGroups.some(g => g.id === groupId)) {
          try {
            await axios.delete(`${API}/groups/${groupId}/members/${selectedUser.id}`, {
              headers: { Authorization: `Bearer ${token}` }
            });
          } catch (err) {
            console.error(`Failed to remove from group ${groupId}:`, err);
          }
        }
      }
      
      // Add to new groups
      for (const groupId of newGroups) {
        if (!oldGroups.includes(groupId)) {
          try {
            await axios.post(`${API}/groups/${groupId}/members/${selectedUser.id}`, {}, {
              headers: { Authorization: `Bearer ${token}` }
            });
          } catch (err) {
            console.error(`Failed to add to group ${groupId}:`, err);
          }
        }
      }
      
      toast.success('Məlumatlar yeniləndi');
      setShowEditUserDialog(false);
      setSelectedUser(null);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Məlumatları yeniləmək mümkün olmadı');
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    try {
      await axios.patch(`${API}/admin/users/${selectedUser.id}/password`, 
        { new_password: newPassword },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Parol dəyişdirildi');
      setShowPasswordDialog(false);
      setSelectedUser(null);
      setNewPassword('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Parolu dəyişdirmək mümkün olmadı');
    }
  };

  const openEditDialog = (u) => {
    setSelectedUser(u);
    // Filter user's groups to only show admin's groups
    const userGroupsInMyAdmin = (u.groups || []).filter(gId => 
      myAdminGroups.some(g => g.id === gId)
    );
    // Get user's roles (from roles array or single role)
    const userRoles = u.roles && u.roles.length > 0 ? u.roles : (u.role ? [u.role] : []);
    setEditUser({
      full_name: u.full_name || '',
      phone: u.phone || '',
      email: u.email || '',
      roles: userRoles,
      groups: userGroupsInMyAdmin,
      modules: u.modules || []
    });
    setShowEditUserDialog(true);
  };

  const openPasswordDialog = (u) => {
    setSelectedUser(u);
    setNewPassword('');
    setShowPasswordDialog(true);
  };

  const handleDeleteUser = async (userId, username) => {
    if (!window.confirm(`${username} istifadəçisini silmək istədiyinizə əminsiniz?`)) {
      return;
    }
    
    try {
      await axios.delete(`${API}/admin/users/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('İstifadəçi silindi');
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'İstifadəçini silmək mümkün olmadı');
    }
  };

  const handleTechnicianClick = async (technicianId, technicianName) => {
    try {
      const params = new URLSearchParams();
      if (period !== 'custom') {
        params.append('period', period);
      } else if (startDate && endDate) {
        params.append('start_date', startDate);
        params.append('end_date', endDate);
      }
      
      const response = await axios.get(`${API}/technicians/${technicianId}/tickets?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setSelectedTechnician({ id: technicianId, name: technicianName });
      setTechnicianTickets(response.data);
      setShowTechTicketsDialog(true);
    } catch (error) {
      toast.error('Ticketləri yükləmək mümkün olmadı');
    }
  };

  const handleExportExcel = async () => {
    if (!selectedTechnician) return;
    
    try {
      const params = new URLSearchParams();
      if (period !== 'custom') {
        params.append('period', period);
      } else if (startDate && endDate) {
        params.append('start_date', startDate);
        params.append('end_date', endDate);
      }
      
      const response = await axios.get(`${API}/technicians/${selectedTechnician.id}/tickets/export?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `tickets_${selectedTechnician.name.replace(' ', '_')}_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('Excel faylı yükləndi');
    } catch (error) {
      toast.error('Excel ixrac etmək mümkün olmadı');
    }
  };

  const fetchGroups = async () => {
    try {
      // Accountant and SuperAdmin see all groups, Admin sees only their groups
      const endpoint = readOnly ? `${API}/groups` : `${API}/groups/my-admin-groups`;
      const response = await axios.get(endpoint, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMyAdminGroups(response.data);
      setGroups(response.data);
    } catch (error) {
      console.error('Groups fetch error:', error);
    }
  };

  useEffect(() => {
    fetchGroups();
    if (!readOnly) {
      fetchMyAdminGroups();
    }
  }, []);

  useEffect(() => {
    fetchData().finally(() => setLoading(false));
    if (activeTab === 'users' && !readOnly) {
      fetchUsers();
    }
    if (activeTab === 'tickets') {
      fetchTickets();
    }
  }, [period, startDate, endDate, activeTab, selectedGroupIds]);

  // Ticket kategorileri
  const pendingTickets = tickets.filter(t => t.status === 'pending');
  const assignedTickets = tickets.filter(t => ['assigned', 'in_progress'].includes(t.status));
  const completedTickets = tickets.filter(t => t.status === 'completed');
  const closedTickets = tickets.filter(t => t.status === 'closed');

  if (loading) {
    return <div className="flex items-center justify-center min-h-screen">Yüklənir...</div>;
  }

  return (
    <div className="min-h-screen bg-zinc-50">
      {/* Header */}
      <header className="bg-white border-b border-zinc-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold uppercase tracking-tight text-zinc-900" data-testid="admin-dashboard-title">
                {readOnly ? 'Mühasib Paneli' : 'Admin Paneli'}
              </h1>
              <p className="text-sm text-zinc-600 mt-1">Xoş gəldiniz, {user.full_name || user.username}</p>
            </div>
            <div className="flex items-center gap-2">
              <TopBarTasksButton onOpenTasksPage={() => setActiveTab('tasks')} />
              <NotificationBell type="broadcast" />
              <Button
                onClick={onLogout}
                variant="outline"
                className="flex items-center gap-2"
                data-testid="logout-button"
              >
                <LogOut className="h-4 w-4" />
                Çıxış
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Tab Buttons */}
        <div className="flex flex-wrap gap-2 mb-6">
          <Button
            onClick={() => setActiveTab('statistics')}
            className={`${activeTab === 'statistics' ? 'bg-primary text-white' : 'bg-white text-zinc-900 hover:bg-zinc-100'}`}
            data-testid="tab-statistics"
          >
            <Trophy className="h-4 w-4 mr-2" />
            Statistika
          </Button>
          {(user?.role === 'accountant' || (user?.modules || []).includes('accounting') || user?.is_superadmin) && (
            <Button
              onClick={() => setActiveTab('accounting')}
              className={`${activeTab === 'accounting' ? 'bg-emerald-600 text-white' : 'bg-white text-zinc-900 hover:bg-zinc-100'}`}
              data-testid="tab-accounting"
            >
              💰 Mühasibatlıq
            </Button>
          )}
          {!readOnly && (
            <>
              <Button
                onClick={() => setActiveTab('tickets')}
                className={`${activeTab === 'tickets' ? 'bg-primary text-white' : 'bg-white text-zinc-900 hover:bg-zinc-100'}`}
                data-testid="tab-tickets"
              >
                <Ticket className="h-4 w-4 mr-2" />
                Ticketlər
              </Button>
              <Button
                onClick={() => setActiveTab('connections')}
                className={`${activeTab === 'connections' ? 'bg-primary text-white' : 'bg-white text-zinc-900 hover:bg-zinc-100'}`}
                data-testid="tab-connections"
              >
                <UserPlus className="h-4 w-4 mr-2" />
                Qoşulmalar
              </Button>
              <Button
                onClick={() => setActiveTab('d2d')}
                className={`${activeTab === 'd2d' ? 'bg-orange-600 text-white' : 'bg-white text-zinc-900 hover:bg-zinc-100'}`}
                data-testid="tab-d2d"
              >
                <Users className="h-4 w-4 mr-2" />
                D2D Qeydləri
              </Button>
              <Button
                onClick={() => setActiveTab('groups')}
                className={`${activeTab === 'groups' ? 'bg-teal-600 text-white' : 'bg-white text-zinc-900 hover:bg-zinc-100'}`}
                data-testid="tab-groups"
              >
                <MapPin className="h-4 w-4 mr-2" />
                Qruplar
              </Button>
              <Button
                onClick={() => setActiveTab('users')}
                className={`${activeTab === 'users' ? 'bg-primary text-white' : 'bg-white text-zinc-900 hover:bg-zinc-100'}`}
                data-testid="tab-users"
              >
                <Users className="h-4 w-4 mr-2" />
                İstifadəçilər
              </Button>
              <Button
                onClick={() => setActiveTab('tasks')}
                className={`${activeTab === 'tasks' ? 'bg-purple-600 text-white' : 'bg-white text-zinc-900 hover:bg-zinc-100'}`}
                data-testid="tab-tasks-admin"
              >
                <ListChecks className="h-4 w-4 mr-2" />
                Tapşırıqlar
              </Button>
              <Button
                onClick={() => setActiveTab('online')}
                className={`${activeTab === 'online' ? 'bg-green-600 text-white' : 'bg-white text-zinc-900 hover:bg-zinc-100'}`}
                data-testid="tab-online-admin"
              >
                <Wifi className="h-4 w-4 mr-2" />
                Online
              </Button>
            </>
          )}
        </div>

        {/* Statistics Tab */}
        {activeTab === 'statistics' && (
          <>
        {/* Report Filters */}
        <StatisticsFilters
          groups={myAdminGroups}
          selectedGroupIds={selectedGroupIds}
          setSelectedGroupIds={setSelectedGroupIds}
          period={period}
          setPeriod={setPeriod}
          startDate={startDate}
          setStartDate={setStartDate}
          endDate={endDate}
          setEndDate={setEndDate}
          displayDateRange={displayDateRange}
          showGroupFilter={true}
        />
        
        {/* Services/Connections/D2D Sub-tabs */}
        <div className="flex gap-2 mb-6">
          <Button
            onClick={() => setStatsSubTab('services')}
            className={`${statsSubTab === 'services' ? 'bg-blue-600 text-white' : 'bg-white text-zinc-900 hover:bg-zinc-100 border'}`}
          >
            <Wrench className="h-4 w-4 mr-2" />
            Servislər
          </Button>
          <Button
            onClick={() => setStatsSubTab('connections')}
            className={`${statsSubTab === 'connections' ? 'bg-green-600 text-white' : 'bg-white text-zinc-900 hover:bg-zinc-100 border'}`}
          >
            <Phone className="h-4 w-4 mr-2" />
            Qoşulmalar
          </Button>
          <Button
            onClick={() => setStatsSubTab('d2d')}
            className={`${statsSubTab === 'd2d' ? 'bg-orange-600 text-white' : 'bg-white text-zinc-900 hover:bg-zinc-100 border'}`}
          >
            <UserPlus className="h-4 w-4 mr-2" />
            D2D
          </Button>
          <Button
            onClick={() => setStatsSubTab('performance')}
            className={`${statsSubTab === 'performance' ? 'bg-purple-600 text-white' : 'bg-white text-zinc-900 hover:bg-zinc-100 border'}`}
            data-testid="subtab-performance"
          >
            <Trophy className="h-4 w-4 mr-2" />
            Texnik Performans
          </Button>
          <Button
            onClick={() => setStatsSubTab('map')}
            className={`${statsSubTab === 'map' ? 'bg-teal-600 text-white' : 'bg-white text-zinc-900 hover:bg-zinc-100 border'}`}
            data-testid="subtab-map"
          >
            <MapPin className="h-4 w-4 mr-2" />
            Xəritə
          </Button>
          <Button
            onClick={() => setStatsSubTab('hr')}
            className={`${statsSubTab === 'hr' ? 'bg-cyan-600 text-white' : 'bg-white text-zinc-900 hover:bg-zinc-100 border'}`}
            data-testid="subtab-hr"
          >
            <Clock className="h-4 w-4 mr-2" />
            HR
          </Button>
          <Button
            onClick={() => setStatsSubTab('zones')}
            className={`${statsSubTab === 'zones' ? 'bg-emerald-600 text-white' : 'bg-white text-zinc-900 hover:bg-zinc-100 border'}`}
            data-testid="subtab-zones"
          >
            <MapPin className="h-4 w-4 mr-2" />
            İş Əraziləri
          </Button>
          <Button
            onClick={() => setStatsSubTab('broadcast')}
            className={`${statsSubTab === 'broadcast' ? 'bg-purple-600 text-white' : 'bg-white text-zinc-900 hover:bg-zinc-100 border'}`}
            data-testid="subtab-broadcast"
          >
            <Bell className="h-4 w-4 mr-2" />
            Bildirişlər
          </Button>
        </div>
        
        {/* Services Statistics */}
        {statsSubTab === 'services' && statistics && (
          <>
        <ServiceStatsCards statistics={statistics} />
        <ServiceStatusBreakdown statistics={statistics} />
        <CategoryBreakdown statistics={statistics} />
        <TechnicianLeaderboard 
          scores={scores} 
          onTechnicianClick={handleTechnicianClick}
        />
        <OperatorLeaderboard scores={operatorScores} />
        <ScoringRules />
        </>
        )}

        {/* Connections Statistics Sub-tab */}
        {statsSubTab === 'connections' && (
          <div className="space-y-6">
            <div className="bg-white p-6 rounded-lg border border-zinc-200">
              <h2 className="text-xl font-bold mb-6 text-zinc-900 flex items-center gap-2">
                <Phone className="h-6 w-6 text-green-600" />
                Yeni Qoşulmalar Statistikası
              </h2>
              <ConnectionStatsCards connectionStats={connectionStats} />
              <ConnectionStatusBreakdown connectionStats={connectionStats} />
            </div>
            <ConnectionByGroupBreakdown connectionStats={connectionStats} />
          </div>
        )}

        {/* D2D Statistics Sub-tab */}
        {statsSubTab === 'd2d' && (
          <div className="space-y-6">
            <div className="bg-white p-6 rounded-lg border border-zinc-200">
              <h2 className="text-xl font-bold mb-6 text-zinc-900 flex items-center gap-2">
                <UserPlus className="h-6 w-6 text-orange-600" />
                D2D Qeydləri Statistikası
              </h2>
              <D2DStatsCards d2dStats={d2dStats} />
            </div>
            <D2DTopAgents d2dStats={d2dStats} />
          </div>
        )}

        {/* Texnik Performans Sub-tab */}
        {statsSubTab === 'performance' && (
          <TechnicianPerformancePanel user={user} />
        )}

        {/* Xəritə Sub-tab */}
        {statsSubTab === 'map' && (
          <MapPanel user={user} />
        )}

        {/* HR Sub-tab */}
        {statsSubTab === 'hr' && (
          <HRPanel user={user} />
        )}

        {/* Work Zones Sub-tab */}
        {statsSubTab === 'zones' && (
          <WorkZonesPanel />
        )}

        {/* Broadcast Sub-tab */}
        {statsSubTab === 'broadcast' && (
          <BroadcastPanel user={user} />
        )}
        </>
        )}

        {/* Accounting Tab */}
        {activeTab === 'accounting' && (
          <AccountingPanel user={user} />
        )}

        {/* Tickets Tab */}
        {activeTab === 'tickets' && (
          <TicketManagementPanel
            pendingTickets={pendingTickets}
            completedTickets={completedTickets}
            assignedTickets={assignedTickets}
            closedTickets={closedTickets}
            technicians={technicians}
            onAssignTechnician={handleAssignTechnician}
            onCloseTicket={handleCloseTicket}
            onShowRejectDialog={handleShowRejectDialog}
          />
        )}

        {/* Users Tab */}
        {activeTab === 'users' && (
          <UserManagementPanel
            users={users}
            currentUserId={user.id}
            showCreateUserDialog={showCreateUserDialog}
            setShowCreateUserDialog={setShowCreateUserDialog}
            newUser={newUser}
            setNewUser={setNewUser}
            onCreateUser={handleCreateUser}
            showEditUserDialog={showEditUserDialog}
            setShowEditUserDialog={setShowEditUserDialog}
            selectedUser={selectedUser}
            editUser={editUser}
            setEditUser={setEditUser}
            onEditUser={handleEditUser}
            onOpenEditDialog={openEditDialog}
            showPasswordDialog={showPasswordDialog}
            setShowPasswordDialog={setShowPasswordDialog}
            newPassword={newPassword}
            setNewPassword={setNewPassword}
            onChangePassword={handleChangePassword}
            onOpenPasswordDialog={openPasswordDialog}
            onDeleteUser={handleDeleteUser}
            availableRoles={['operator', 'technician', 'd2d_agent', 'lawyer', 'network_admin', 'system_admin']}
            availableGroups={myAdminGroups}
            showGroupSelection={true}
          />
        )}

        {/* Technician Tickets Dialog */}
        <Dialog open={showTechTicketsDialog} onOpenChange={setShowTechTicketsDialog}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-2xl font-bold flex items-center justify-between">
                <span>{selectedTechnician?.name} - Ticketlər</span>
                <Button
                  onClick={handleExportExcel}
                  variant="outline"
                  className="flex items-center gap-2"
                  data-testid="export-excel-button"
                >
                  <FileSpreadsheet className="h-4 w-4" />
                  Excel
                </Button>
              </DialogTitle>
            </DialogHeader>
            
            {technicianTickets && (
              <div className="space-y-4">
                {/* Summary */}
                <div className="grid grid-cols-3 gap-4">
                  <div className="bg-zinc-100 p-4 rounded-lg text-center">
                    <p className="text-2xl font-bold text-zinc-900">{technicianTickets.total}</p>
                    <p className="text-sm text-zinc-600">Ümumi</p>
                  </div>
                  <div className="bg-green-100 p-4 rounded-lg text-center">
                    <p className="text-2xl font-bold text-green-700">{technicianTickets.closed}</p>
                    <p className="text-sm text-green-600">Bağlanmış</p>
                  </div>
                  <div className="bg-red-100 p-4 rounded-lg text-center">
                    <p className="text-2xl font-bold text-red-700">{technicianTickets.rejected}</p>
                    <p className="text-sm text-red-600">Geri qaytarılmış</p>
                  </div>
                </div>
                
                {/* Ticket List */}
                <div className="border border-zinc-200 rounded-lg overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-zinc-100">
                      <tr>
                        <th className="px-4 py-2 text-left">ID</th>
                        <th className="px-4 py-2 text-left">Abunəçi</th>
                        <th className="px-4 py-2 text-left">Kateqoriya</th>
                        <th className="px-4 py-2 text-center">Status</th>
                        <th className="px-4 py-2 text-right">Tarix</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-zinc-200">
                      {technicianTickets.tickets.map((ticket) => (
                        <tr key={ticket.id} className="hover:bg-zinc-50">
                          <td className="px-4 py-2 font-mono text-xs">{ticket.id.slice(0, 8)}</td>
                          <td className="px-4 py-2">{ticket.subscriber_name}</td>
                          <td className="px-4 py-2 capitalize">{ticket.category}</td>
                          <td className="px-4 py-2 text-center">
                            <span className={`inline-flex px-2 py-1 rounded-full text-xs font-semibold ${
                              ticket.status === 'closed' ? 'bg-green-100 text-green-700' :
                              ticket.previous_technician_id === selectedTechnician?.id ? 'bg-red-100 text-red-700' :
                              'bg-zinc-100 text-zinc-700'
                            }`}>
                              {ticket.status === 'closed' ? 'Bağlandı' : 
                               ticket.previous_technician_id === selectedTechnician?.id ? 'Geri qaytarıldı' : 
                               ticket.status}
                            </span>
                          </td>
                          <td className="px-4 py-2 text-right text-xs text-zinc-600">
                            {new Date(ticket.created_at).toLocaleDateString('az-AZ')}
                          </td>
                        </tr>
                      ))}
                      {technicianTickets.tickets.length === 0 && (
                        <tr>
                          <td colSpan="5" className="px-4 py-8 text-center text-zinc-500">
                            Bu dövr üçün ticket tapılmadı
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Rejection Reason Dialog */}
        <Dialog open={showRejectDialog} onOpenChange={setShowRejectDialog}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="text-xl font-bold text-zinc-900">
                Ticketi geri qaytar
              </DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4">
              {/* Warning */}
              <div className="p-4 rounded-lg border bg-red-50 border-red-200">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="h-5 w-5 mt-0.5 text-red-600" />
                  <div>
                    <p className="font-semibold text-red-700">
                      Texnikdən -20 xal silinəcək
                    </p>
                    <p className="text-sm text-zinc-600 mt-1">
                      Texnik: {rejectTicket?.assigned_technician_name}
                    </p>
                  </div>
                </div>
              </div>
              
              {/* Rejection Reason */}
              <div>
                <Label htmlFor="admin_rejection_reason">Geri qaytarma səbəbi *</Label>
                <Textarea
                  id="admin_rejection_reason"
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  placeholder="Məsələn: İş yarımçıq qalıb, Şəkil keyfiyyəti aşağıdır..."
                  className="mt-1.5"
                  rows={3}
                  data-testid="admin-rejection-reason-input"
                />
              </div>
              
              {/* Quick reasons */}
              <div className="flex flex-wrap gap-2">
                {['İş yarımçıq qalıb', 'Şəkil göndərilməyib', 'Müştəri şikayət edib', 'Texniki problem həll olunmayıb'].map((reason) => (
                  <Button
                    key={reason}
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setRejectionReason(reason)}
                    className="text-xs"
                  >
                    {reason}
                  </Button>
                ))}
              </div>
            </div>
            
            <DialogFooter className="gap-2">
              <Button
                variant="outline"
                onClick={() => setShowRejectDialog(false)}
                data-testid="admin-cancel-reject-button"
              >
                Ləğv et
              </Button>
              <Button
                onClick={handleRejectTicket}
                disabled={!rejectionReason.trim()}
                className="bg-red-600 hover:bg-red-700 text-white"
                data-testid="admin-confirm-reject-button"
              >
                <XCircle className="h-4 w-4 mr-2" />
                Geri qaytar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Connections Tab */}
        <div style={{ display: activeTab === 'connections' ? 'block' : 'none' }}>
          <ConnectionsPanel user={user} />
        </div>

        {/* D2D Tab */}
        <div style={{ display: activeTab === 'd2d' ? 'block' : 'none' }}>
          <D2DPanel user={user} />
        </div>

        {/* Groups Tab */}
        {activeTab === 'groups' && (
          <AdminGroupsPanel token={token} />
        )}

        {/* Tasks Tab (Sprint 10-11) */}
        {activeTab === 'tasks' && (
          <TasksPanel user={user} />
        )}

        {/* Online Tab — Zabbix Live Network Metrics (Group Admin sees only own VLANs) */}
        {activeTab === 'online' && (
          <OnlinePanel user={user} />
        )}
      </div>
    </div>
  );
}
